import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

import Paper from '@mui/material/Paper';

import { useAppDispatch, useAppSelector } from '../../../../app/store';
import reducerPath from '../../../../app/reducerPath';
import {
  LogisticTasksEnum,
  logisticTasksPetrolResourceActions,
} from '../../../../entities/logistic/logisticTasks/redux/logisticTasksPetrol.slice';
import DeliveryReorderingComponent from './components/DeliveryReorderingComponent/DeliveryReorderingComponent';
import EnhancedTableToolbar from './components/EnhancedTableToolbar/EnhancedTableToolbar';
import EnhancedTableHead from './components/EnhancedTableHead/EnhancedTableHead';
import OrderRow from './components/OrderRow/OrderRow';

export default function PetrolDeliveryMainTableWidget({ reorderLogisticTask, searchOrder, setSearchOrder }) {
  const dispatch = useAppDispatch();
  const logisticTask = useAppSelector((state) => state[`${reducerPath.logisticTasksPetrolResource}/counter`]);
  const { selectedVehicles } = useAppSelector((state) => state[`${reducerPath.logisticTasksPetrolResource}/counter`]);

  const [showReordering, setShowReordering] = React.useState(false);

  const vehicles = React.useMemo(
    () => logisticTask?.data?.sourceWithSolution?.availableTankers || [],
    [logisticTask?.data?.sourceWithSolution?.availableTankers],
  );

  const orderPoints = React.useMemo(() => {
    const points = logisticTask?.data?.sourceWithSolution?.points
      ? Object.values(logisticTask?.data?.sourceWithSolution?.points)
      : [];
    return points.reduce((acc, p) => {
      const prevOrderObj = acc[p.orderNumber] || {};
      prevOrderObj.loading = p.isLoading ? p : prevOrderObj.loading;
      prevOrderObj.unloading = p.isUnloading ? p : prevOrderObj.unloading;
      return p.orderNumber ? { ...acc, [p.orderNumber]: prevOrderObj } : acc;
    }, {});
  }, [logisticTask?.data?.sourceWithSolution?.points]);

  const sortedOrders = React.useMemo(() => {
    const newOrders = logisticTask?.data?.sourceWithSolution?.orderSourceList || [];
    let res = [...newOrders];

    if (selectedVehicles?.length) {
      res = res.filter((order) => selectedVehicles?.includes(order.vehicleNumber));
    }

    return res.sort((a, b) => {
      const loadingA = a.loadingTime ? new Date(a.loadingTime).getTime() : Infinity;
      const loadingB = b.loadingTime ? new Date(b.loadingTime).getTime() : Infinity;
      const deliveryA = a.deliveryTime ? new Date(a.deliveryTime).getTime() : Infinity;
      const deliveryB = b.deliveryTime ? new Date(b.deliveryTime).getTime() : Infinity;

      return loadingA === loadingB ? deliveryA - deliveryB : loadingA - loadingB;
    });
  }, [logisticTask?.data?.sourceWithSolution?.orderSourceList, selectedVehicles]);

  const orders = React.useMemo(() => {
    if (searchOrder.length < 3) return sortedOrders;

    const query = searchOrder.toLowerCase();

    return sortedOrders.filter((o) => {
      return [o.addressLoading, o.addressUnloading]
        .map((field) => (field ? String(field).toLowerCase() : ''))
        .some((field) => field.includes(query));
    });
  }, [sortedOrders, searchOrder]);

  const selectedOrders = logisticTask?.selectedOrders;

  const allErrorsLength = React.useMemo(
    () =>
      orders?.reduce((prev, order) => {
        let sum = prev;
        const len = Number(order?.statuses?.length);
        if (!Number.isNaN(len)) {
          sum += len;
        }
        return sum;
      }, 0),
    [orders],
  );

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const [selected, setSelected] = React.useState([]);

  const onSelectToStore = (items) => {
    dispatch(
      logisticTasksPetrolResourceActions.setSelectedOrders({
        [LogisticTasksEnum.selectedOrders]: items,
      }),
    );
  };

  React.useEffect(() => {
    if (!selectedOrders?.length) {
      setSelected([]);
    }
  }, [selectedOrders]);

  const handleClickRow = (row, forceSelect) => {
    let newSelected = [];
    if (selectedOrders.includes(row.orderNumber) && !forceSelect) {
      newSelected = [];
    } else {
      newSelected = [row.orderNumber];
    }
    setSelected(newSelected);
    onSelectToStore(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - orders.length) : 0;

  const assignVehicle = (vehicle, row) => {
    const newData = JSON.parse(JSON.stringify(logisticTask));
    if (vehicle) {
      if (!newData.data.solution.routes?.length) {
        newData.data.solution.routes = [];
      }
      let newRoute = newData.data.solution.routes?.find((r) => r.tanker.registrationNum === vehicle.registrationNum);
      if (!newRoute) {
        newRoute = {
          orders: [],
          tanker: vehicle,
        };
        newData.data.solution.routes.push(newRoute);
      }
      if (row.vehicleNumber) {
        const route = newData.data.solution.routes.find((r) => r.tanker.registrationNum === row.vehicleNumber);
        const theOrderIndex = route.orders.findIndex((o) => o.orderSource?.requestId === row.id);
        newRoute.orders.push(route.orders[theOrderIndex]);
        route.orders.splice(theOrderIndex, 1);
      } else {
        const theOrderIndex = newData.data.solution.notClosedRequests.findIndex((o) => o.requestId === row.id);
        newRoute.orders.push({ orderSource: newData.data.solution.notClosedRequests[theOrderIndex] });
        newData.data.solution.notClosedRequests.splice(theOrderIndex, 1);
      }
    } else if (row.vehicleNumber) {
      const route = newData.data.solution.routes.find((r) => r.tanker.registrationNum === row.vehicleNumber);
      const theOrderIndex = route.orders.findIndex((o) => o.orderSource?.requestId === row.id);
      newData.data.solution.notClosedRequests.push(route.orders[theOrderIndex]);
      route.orders.splice(theOrderIndex, 1);
    }
    dispatch(logisticTasksPetrolResourceActions.setVehicleAssigned(newData.data));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }} elevation={1}>
        <EnhancedTableToolbar
          allErrorsLength={allErrorsLength}
          showReordering={showReordering}
          setShowReordering={setShowReordering}
          onChangeSearchOrder={setSearchOrder}
          searchOrder={searchOrder}
        />
        {showReordering ? (
          <>
            {vehicles
              .filter((v) => (selectedVehicles?.length ? selectedVehicles.includes(v.registrationNum) : true))
              .map((v) => (
                <DeliveryReorderingComponent
                  key={v.id}
                  color={v.color}
                  vehicle={v}
                  reorderLogisticTask={reorderLogisticTask}
                  orderPoints={orderPoints}
                />
              ))}
          </>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ width: '100%' }} aria-labelledby="tableTitle" size="small">
                <EnhancedTableHead />
                <TableBody>
                  {(rowsPerPage > 0 ? orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : orders)?.map(
                    (row, index) => {
                      const labelId = `enhanced-table-checkbox-${index}`;
                      const key = `${row.id}-${index}`;
                      const isItemSelected = isSelected(row.id);

                      return (
                        <OrderRow
                          key={key}
                          row={row}
                          index={index}
                          labelId={labelId}
                          isItemSelected={isItemSelected}
                          handleClickRow={handleClickRow}
                          vehicles={vehicles}
                          assignVehicle={assignVehicle}
                          dispatch={dispatch}
                          orderPoints={orderPoints}
                        />
                      );
                    },
                  )}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 33 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[15, 50, 100]}
              component="div"
              count={orders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
PetrolDeliveryMainTableWidget.propTypes = {
  searchOrder: PropTypes.string.isRequired,
  setSearchOrder: PropTypes.func.isRequired,
  reorderLogisticTask: PropTypes.func.isRequired,
};
